import { t } from '@lingui/core/macro';
import { SourcingLineItemDTO } from '@luminovo/api-contracts';
import { isPresent, QuantityHelper } from '@luminovo/commons';
import { colorSystem, Flexbox, Link, NonIdealState, Text } from '@luminovo/design-system';
import { formatDecimal, formatToLongDate, transEnum } from '@luminovo/frontend-utils';
import { Box } from '@mui/material';
import type { JSX } from 'react';

import {
    formatOfferLinkedLocation,
    formatOfferOriginWithContext,
    formatPackaging,
    formatPartLite,
    formatQuantity,
} from '../../formatters';
import { priceTypeTranslations } from '../../i18n';
import { isStandardPartOffer } from '../../model';
import { AvailabilityChip } from '../chips/AvailabilityChip';
import { OfferChips } from '../chips/OfferChips';
import { SolutionStatusIcon } from '../solutionDetails/SolutionStatusIcon';
import { CompareLineItemActionButton, CompareLineItemVariant } from './CompareLineItemActionButton';
import { CompareLineItemCostBreakdown } from './CompareLineItemCostBreakdown';
import { CompareLineItemPriceBreaks } from './CompareLineItemPriceBreaks';

interface CompareLineItemCardProps {
    sourcingLineItem: SourcingLineItemDTO;
    scenarioName: string;
    scenarioUrl?: string;
    /** Set when the offer came from a quote request. */
    quoteRequestUrl?: string;
    /** Whether this is the baseline scenario. */
    isCurrent?: boolean;
    /** Whether this card has the baseline's offer selected. */
    isSynced?: boolean;
    onApply?: (copyManualStates: boolean) => void;
    isApplying?: boolean;
    onRunSelection?: () => void;
    isRunningSelection?: boolean;
    /** Replaces the apply button, e.g. with the baseline scenario's label. */
    footerContent?: React.ReactNode;
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }): JSX.Element | null {
    if (!isPresent(value)) {
        return null;
    }

    return (
        <Flexbox
            gap={12}
            sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '24px',
            }}
        >
            <Text variant="body" color={colorSystem.neutral[6]} style={{ flexShrink: 0 }}>
                {label}
            </Text>
            <Box
                sx={{
                    textAlign: 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                }}
            >
                {typeof value === 'string' ? (
                    <Text
                        variant="body"
                        color={colorSystem.neutral[9]}
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            textAlign: 'right',
                        }}
                    >
                        {value}
                    </Text>
                ) : (
                    value
                )}
            </Box>
        </Flexbox>
    );
}

export function CompareLineItemCard({
    sourcingLineItem,
    scenarioName,
    scenarioUrl,
    quoteRequestUrl,
    isCurrent = false,
    isSynced = false,
    onApply,
    isApplying = false,
    onRunSelection,
    isRunningSelection = false,
    footerContent,
}: CompareLineItemCardProps): JSX.Element {
    const variant: CompareLineItemVariant = isCurrent ? 'current' : isSynced ? 'synced' : 'apply';

    const offer = sourcingLineItem.offer;
    const selectedPart = sourcingLineItem.selected_part;
    const aggregatedQuantity = sourcingLineItem.quantities.aggregated_quantity;
    const scrapQuantity = { amount: QuantityHelper.from(sourcingLineItem.quantities.scrap_quantity).parseValue() };
    const costSummary = sourcingLineItem.cost_summary;
    const availability = sourcingLineItem.availability;
    const notes = sourcingLineItem.solution_summary.notes;
    const sourcedBy = sourcingLineItem.sourced_by;
    const offerIdAndSignature = sourcingLineItem.offer_id_and_signature;

    const lineItemStatus = sourcingLineItem.line_item_status;
    const solutionTags = sourcingLineItem.solution_summary.solution_tags;
    const effectiveStatus = sourcingLineItem.solution_summary.status.effective;

    const standardOffer = offer && isStandardPartOffer(offer) ? offer : undefined;
    const supplierName = offer ? formatOfferLinkedLocation(offer.linked_location) : undefined;
    const sku = offer?.supplier_part_number;
    const packaging = standardOffer?.packaging;
    const priceType = offer?.price_type;
    const offerOrigin = offer?.origin;
    const creationDate = offer?.creation_date;

    const validFrom = offer?.valid_from;
    const validUntil = offer?.valid_until;
    const stock = standardOffer?.availability.stock;
    const offerNotes = offer?.notes;

    const priceBreaks = offer ? offer.price_breaks : [];

    const signature = offerIdAndSignature?.signature;
    const selectedMoq = signature && 'moq' in signature ? signature.moq : undefined;
    const selectedMpq = signature && 'mpq' in signature ? signature.mpq : undefined;

    const hasOffer = isPresent(offer);

    return (
        <Box
            sx={{
                width: '390px',
                minWidth: '390px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${isCurrent ? colorSystem.primary[4] : colorSystem.neutral[2]}`,
                borderRadius: '8px',
                backgroundColor: colorSystem.neutral.white,
                overflow: 'hidden',
            }}
        >
            <Flexbox
                gap={8}
                sx={{
                    flexDirection: 'column',
                    padding: '10px 12px',
                    borderBottom: `1px solid ${isCurrent ? colorSystem.primary[4] : colorSystem.neutral[2]}`,
                    backgroundColor: isCurrent ? colorSystem.primary[2] : colorSystem.neutral[1],
                    flexShrink: 0,
                }}
            >
                <Flexbox
                    gap={8}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Flexbox
                        gap={6}
                        style={{ flex: 1, minWidth: 0 }}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                flexShrink: 0,
                                backgroundColor: colorSystem.neutral.white,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <SolutionStatusIcon status={effectiveStatus} />
                        </Box>
                        {scenarioUrl ? (
                            <Link
                                attention="low"
                                href={scenarioUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    flex: 1,
                                    minWidth: 0,
                                    color: colorSystem.neutral[9],
                                }}
                            >
                                {scenarioName}
                            </Link>
                        ) : (
                            <Text
                                variant="h4"
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {scenarioName}
                            </Text>
                        )}
                    </Flexbox>
                    <Text variant="body-semibold" color={colorSystem.neutral[9]} tabularNums style={{ flexShrink: 0 }}>
                        {formatQuantity(aggregatedQuantity)}
                    </Text>
                </Flexbox>

                {notes && (
                    <Box
                        sx={{
                            maxHeight: '60px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            padding: '6px 8px',
                            backgroundColor: colorSystem.neutral.white,
                            borderRadius: '4px',
                            border: `1px solid ${colorSystem.neutral[2]}`,
                        }}
                    >
                        <Text variant="body-small" color={colorSystem.neutral[8]}>
                            {notes}
                        </Text>
                    </Box>
                )}
            </Flexbox>
            <Box
                sx={{
                    flex: '1 1 0',
                    overflowY: 'auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Flexbox
                    gap={8}
                    sx={{
                        flexDirection: 'column',
                        padding: '10px 12px',
                        flex: '0 0 auto',
                    }}
                >
                    {solutionTags.length > 0 && <OfferChips solution={{ solutionTags }} />}

                    {hasOffer ? (
                        <Flexbox
                            gap={2}
                            sx={{
                                flexDirection: 'column',
                            }}
                        >
                            {selectedPart && <FieldRow label={t`Part`} value={formatPartLite(selectedPart)} />}
                            <FieldRow label={t`Supplier`} value={supplierName} />
                            {sku && <FieldRow label={t`Supplier part number`} value={sku} />}
                            {packaging && <FieldRow label={t`Packaging`} value={formatPackaging(packaging)} />}
                            {priceType && (
                                <FieldRow label={t`Price type`} value={transEnum(priceType, priceTypeTranslations)} />
                            )}
                            {availability && (
                                <FieldRow
                                    label={t`Lead time`}
                                    value={<AvailabilityChip solution={{ availability, solutionTags: [] }} />}
                                />
                            )}
                            {isPresent(stock) && <FieldRow label={t`Stock`} value={formatDecimal(stock)} />}
                            {validFrom && <FieldRow label={t`Valid from`} value={formatToLongDate(validFrom)} />}
                            {validUntil && <FieldRow label={t`Valid until`} value={formatToLongDate(validUntil)} />}
                            {quoteRequestUrl && (
                                <FieldRow
                                    label={t`Quote request`}
                                    value={
                                        <Link href={quoteRequestUrl} target="_blank" rel="noopener noreferrer">
                                            {t`View quote request`}
                                        </Link>
                                    }
                                />
                            )}
                            {offerNotes && (
                                <Box
                                    sx={{
                                        maxHeight: '60px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        padding: '6px 8px',
                                        backgroundColor: colorSystem.neutral[1],
                                        borderRadius: '4px',
                                        marginTop: '4px',
                                    }}
                                >
                                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                                        {offerNotes}
                                    </Text>
                                </Box>
                            )}
                        </Flexbox>
                    ) : (
                        <Flexbox
                            style={{ flex: 1 }}
                            sx={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {sourcedBy === 'External' ? (
                                <NonIdealState
                                    icon={null}
                                    title={t`Excluded from sourcing`}
                                    description={t`This sourcing line item is excluded from sourcing and will have no effect on cost or lead time calculations.`}
                                    style={{ padding: '24px 16px', maxWidth: '100%' }}
                                />
                            ) : lineItemStatus === 'Pending' ? (
                                <NonIdealState
                                    icon={null}
                                    title={t`No solution selected`}
                                    description={t`No solution was selected automatically. You can manually select one or run the offer selection.`}
                                    style={{ padding: '24px 16px', maxWidth: '100%' }}
                                    action={
                                        onRunSelection
                                            ? {
                                                  children: t`Run selection`,
                                                  onClick: onRunSelection,
                                                  disabled: isRunningSelection,
                                              }
                                            : undefined
                                    }
                                />
                            ) : lineItemStatus === 'NoOffer' ? (
                                <NonIdealState
                                    icon={null}
                                    title={t`No approved parts`}
                                    description={t`There are no approved parts for this line item. Go to the BOM and add a part alternative.`}
                                    style={{ padding: '24px 16px', maxWidth: '100%' }}
                                />
                            ) : (
                                <NonIdealState
                                    icon={null}
                                    title={t`No solutions available`}
                                    description={t`We couldn't find any offers for this part.`}
                                    style={{ padding: '24px 16px', maxWidth: '100%' }}
                                />
                            )}
                        </Flexbox>
                    )}

                    {priceBreaks.length > 0 && (
                        <CompareLineItemPriceBreaks
                            priceBreaks={priceBreaks}
                            selectedMoq={selectedMoq}
                            selectedMpq={selectedMpq}
                        />
                    )}

                    {(offerOrigin || creationDate) && (
                        <Text variant="body-small" color={colorSystem.neutral[6]}>
                            {[
                                offer && formatOfferOriginWithContext(offer),
                                creationDate && formatToLongDate(creationDate),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Text>
                    )}
                </Flexbox>
            </Box>
            {costSummary && (
                <Box
                    sx={{
                        flexShrink: 0,
                        borderTop: `1px solid ${colorSystem.neutral[2]}`,
                    }}
                >
                    <CompareLineItemCostBreakdown
                        costSummary={costSummary}
                        aggregatedQuantity={aggregatedQuantity}
                        scrapQuantity={scrapQuantity}
                    />
                </Box>
            )}
            {sourcedBy !== 'External' && (onApply || footerContent || isSynced) && (
                <Flexbox
                    sx={{
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderTop: `1px solid ${isCurrent ? colorSystem.primary[4] : colorSystem.neutral[2]}`,
                        backgroundColor: isCurrent ? colorSystem.primary[2] : colorSystem.neutral[1],
                        flexShrink: 0,
                        minHeight: '52px',
                    }}
                >
                    {footerContent ?? (
                        <CompareLineItemActionButton
                            variant={variant}
                            scenarioName={scenarioName}
                            onApply={onApply!}
                            isLoading={isApplying}
                            disabled={false}
                        />
                    )}
                </Flexbox>
            )}
            {sourcedBy === 'External' && (
                <Flexbox
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px 12px',
                        borderTop: `1px solid ${colorSystem.primary[4]}`,
                        backgroundColor: colorSystem.primary[2],
                        flexShrink: 0,
                        minHeight: '52px',
                    }}
                >
                    <Text variant="body-small" color={colorSystem.primary[7]}>
                        {t`Excluded from sourcing`}
                    </Text>
                </Flexbox>
            )}
        </Box>
    );
}
